import type { GridColDef } from "@mui/x-data-grid-premium";
import type { ReactNode } from "react";
import { useCallback } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { ObjectFieldValues } from "../../../api/types/objectTypes";
import type { ExcelExportConfig } from "./StartObjectsExportConfirmationDialog";
import ObjectsListPageContainer from "./ObjectsListPageContainer";

interface Props {
  objectType: string;
  objectDetailsPathPattern: string;
  objectsTitle?: string;
  showLogos?: boolean;
  allowCreate?: boolean;
  disablePageTitleUpdate?: boolean;
  excelExportConfig?: ExcelExportConfig;
  headerActions?: ReactNode;
  actionsColumn?: GridColDef<ObjectFieldValues>;
  selectionActions?: ReactNode;
  onTabChange?: () => void;
}

const ObjectsListPage = ({
  objectType,
  objectDetailsPathPattern,
  objectsTitle,
  showLogos,
  allowCreate,
  disablePageTitleUpdate,
  excelExportConfig,
  headerActions,
  actionsColumn,
  selectionActions,
  onTabChange,
}: Props) => {
  const getObjectDefinition = useCallback(() => adminApi.getObjectDefinition(objectType), [objectType]);

  const [objectDefinitionResp, objectDefinitionError] = useFetch(getObjectDefinition);

  const [dataImportOptions, dataImportOptionsError] = useFetch(adminApi.getDataImportOptions);

  if (objectDefinitionError) {
    logError(objectDefinitionError, "[ObjectsListPage] getObjectDefinition");
    return <DataLoadingFailed sx={{ pt: 2.5 }} />;
  }

  if (dataImportOptionsError) {
    logError(dataImportOptionsError, "[ObjectsListPage] getDataImportOptions");
  }

  if (objectDefinitionResp === undefined) {
    return <InlineLoader />;
  }

  const resolvedObjectsTitle = objectsTitle ?? objectDefinitionResp.objectClassDefinition.objectName;

  return (
    <ObjectsListPageContainer
      objectClassDefinition={objectDefinitionResp.objectClassDefinition}
      objectPermissions={objectDefinitionResp.objectPermissions}
      dataImportOptions={dataImportOptions ?? []}
      objectDetailsPathPattern={objectDetailsPathPattern}
      objectsTitle={resolvedObjectsTitle}
      showLogos={showLogos}
      allowCreate={allowCreate}
      disablePageTitleUpdate={disablePageTitleUpdate}
      excelExportConfig={excelExportConfig}
      headerActions={headerActions}
      actionsColumn={actionsColumn}
      selectionActions={selectionActions}
      onTabChange={onTabChange}
    />
  );
};

export default ObjectsListPage;
